// What the flood layers say about a place and how it is named and coloured, shared by the flood
// overlays of the map (their popup) and the flood risk profile widget, so that both always say
// the same.

export interface FloodLayerInfo {
    readonly layer: string;
    readonly scenario: string;
    readonly sourceVersion: string;
    readonly importedAt: string;
}

export interface FloodZoneArea {
    readonly scenario: string;
    readonly hazardCategory: number | null;
    readonly geometry: GeoJSON.Geometry;
}

export interface FloodRiskProfile {
    readonly scenarios: string[];
    readonly hazardCategory: number | null;
    readonly hazardMapAvailable: boolean;
    readonly hazardMapVersion: string | null;
    readonly streamName: string | null;
    readonly distanceToQ100Metres: number | null;
    readonly historicalFloodPeriods: string[];
    // False until the 5-, 20- and 100-year flood areas (the flooding risk is told from) have been imported; the flooding risk is then null.
    readonly floodZonesAvailable: boolean;
    readonly floodingRisk: string | null;
}

export interface ScenarioStyle {
    readonly label: string;
    readonly color: string;
    readonly fillOpacity: number;
}

// The overlays in the order of the layers control: from the most frequent flood to the rarest,
// then the other layers.
export const SCENARIO_ORDER = ['q5', 'q20', 'q100', 'q500', 'active_zone', 'hazard', 'historical_max'] as const;

// The zoom every overlay shows its data from: the hazard map is a two-metre model of two
// million patches, from farther away than the street level it is only noise and megabytes.
export const MIN_ZOOM_BY_SCENARIO: Record<string, number> = {
    q5: 11, q20: 11, q100: 11, q500: 11, active_zone: 11, hazard: 12, historical_max: 11,
};

export const SCENARIOS: Record<string, ScenarioStyle> = {
    q5: {label: '5letá voda (Q5)', color: '#08306b', fillOpacity: 0.45},
    q20: {label: '20letá voda (Q20)', color: '#2171b5', fillOpacity: 0.4},
    q100: {label: '100letá voda (Q100)', color: '#4292c6', fillOpacity: 0.35},
    q500: {label: '500letá voda (Q500)', color: '#9ecae1', fillOpacity: 0.4},
    active_zone: {label: 'Aktivní zóna záplavového území', color: '#cb181d', fillOpacity: 0.3},
    hazard: {label: 'Povodňové ohrožení', color: '#444444', fillOpacity: 0.55},
    historical_max: {label: 'Historické rozlivy', color: '#7a0177', fillOpacity: 0.25},
};

// The designated flood areas by the return period of their flood, as the profile lists them.
export const FLOOD_ZONE_SCENARIOS = ['q5', 'q20', 'q100', 'q500'] as const;

export const FLOOD_ZONE_LABELS: Record<string, string> = {
    q5: '5letá', q20: '20letá', q100: '100letá', q500: '500letá',
};

// The same in the genitive, for "v záplavovém území 100leté vody".
export const FLOOD_ZONE_LABELS_GENITIVE: Record<string, string> = {
    q5: '5leté', q20: '20leté', q100: '100leté', q500: '500leté',
};

// Yellow through orange to red, one hue away from both the greens of the base map (parks, fields,
// which a green residual category vanished into) and the blues of the flood areas.
export const HAZARD_CATEGORIES: Record<number, {label: string; color: string}> = {
    1: {label: 'zbytkové', color: '#ffffb2'},
    2: {label: 'nízké', color: '#fecc5c'},
    3: {label: 'střední', color: '#fd8d3c'},
    4: {label: 'vysoké', color: '#e31a1c'},
};

// The "flooding risk" parameter of an estimate: the most frequent flood reaching the place, in
// the words of the parameter and a colour from green to red.
export const FLOODING_RISK_ORDER = ['minimal', 'low', 'medium', 'high'] as const;

export const FLOODING_RISKS: Record<string, {label: string; description: string; color: string}> = {
    minimal: {label: 'zanedbatelné', description: 'mimo stanovená záplavová území', color: '#8fcf8a'},
    low: {label: 'nízké', description: 'území 100leté vody', color: '#fdc95c'},
    medium: {label: 'střední', description: 'území 20leté vody', color: '#f0632e'},
    high: {label: 'vysoké', description: 'území 5leté vody', color: '#bd0026'},
};

// Said instead of the flooding risk until the 5-, 20- and 100-year flood areas have been imported.
export const FLOOD_ZONES_UNAVAILABLE = 'Záplavová území zatím nejsou načtena, povodňové riziko nelze určit.';

export const ATTRIBUTION = 'Zdroj: VÚV TGM, v.v.i. (DIBAVOD, mapy povodňového ohrožení), MŽP';
export const DISCLAIMER = 'Průběh záplavových území je orientační, závazné je stanovení vodoprávního úřadu.';

const MONTHS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];

export function escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, (character) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;'}[character] ?? character));
}

/** "08_2002" → "srpen 2002", "03-04_2006" → "březen–duben 2006"; anything else as it is. */
export function formatFloodPeriod(period: string): string {
    const match = /^(\d{2})(?:-(\d{2}))?_(\d{4})$/.exec(period);

    if (!match) {
        return period;
    }

    const from = MONTHS[Number(match[1]) - 1];
    const to = match[2] ? MONTHS[Number(match[2]) - 1] : undefined;

    if (!from || (match[2] && !to)) {
        return period;
    }

    return `${from}${to ? `–${to}` : ''} ${match[3]}`;
}

/** The hazard overlay named with the planning cycle its data are the state of. */
export function hazardLabel(version: string | null): string {
    return version ? `${SCENARIOS.hazard.label} (stav ${version})` : SCENARIOS.hazard.label;
}

/** The recorded floods that reached the place, newest first, formatted. */
export function historicalFloods(profile: FloodRiskProfile): string[] {
    return [...profile.historicalFloodPeriods]
        .sort((a, b) => (/\d{4}$/.exec(b)?.[0] ?? b).localeCompare(/\d{4}$/.exec(a)?.[0] ?? a))
        .map(formatFloodPeriod);
}

/** The designated flood areas reaching the place, from the most frequent flood. */
export function floodZonesReaching(profile: FloodRiskProfile): string[] {
    return FLOOD_ZONE_SCENARIOS.filter((scenario) => profile.scenarios.includes(scenario));
}

export function formatMetres(metres: number): string {
    return metres >= 1000
        ? `${(metres / 1000).toLocaleString('cs-CZ', {maximumFractionDigits: 1})} km`
        : `${Math.round(metres).toLocaleString('cs-CZ')} m`;
}
